import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Eye, EyeOff, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useAuth } from '../contexts/AuthContext';
import { createLoginSchema, type LoginFormData } from '../schemas/authSchemas';
import { signInWithGoogle, signInWithApple } from '@/services/oauth';
import { useOAuthSettings } from '@/hooks/useOAuthSettings';

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signIn, resendVerificationEmail, isLoading: authLoading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [emailNotConfirmed, setEmailNotConfirmed] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const verified = searchParams.get('verified') === 'true';

  // Fetch OAuth settings from Supabase
  const { googleEnabled, appleEnabled } = useOAuthSettings();

  const { t } = useTranslation('auth');
  const loginSchema = useMemo(() => createLoginSchema(t), [t]);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // Get redirect URL from query params or localStorage (for invite flow)
  const getRedirectUrl = (): string => {
    const redirectParam = searchParams.get('redirect');
    if (redirectParam) {
      return redirectParam;
    }

    // Check for stored invite token
    const pendingInviteToken = localStorage.getItem('pendingInviteToken');
    if (pendingInviteToken) {
      return `/invite/${pendingInviteToken}`;
    }

    return '/';
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setIsGoogleLoading(true);

    try {
      const result = await signInWithGoogle();
      if (!result.success && result.error) {
        setError(result.error);
      } else if (result.success) {
        // Redirect will happen automatically for web OAuth
        // For native, session is set and we navigate
        const redirectUrl = getRedirectUrl();
        navigate(redirectUrl);
      }
    } catch {
      setError(t('login.googleSignInFailed'));
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setError(null);
    setIsAppleLoading(true);

    try {
      const result = await signInWithApple();
      if (!result.success && result.error) {
        if (result.error !== 'Sign-in cancelled') {
          setError(result.error);
        }
      } else if (result.success) {
        // Redirect will happen automatically for web OAuth
        // For native, session is set and we navigate
        const redirectUrl = getRedirectUrl();
        navigate(redirectUrl);
      }
    } catch {
      setError(t('login.appleSignInFailed'));
    } finally {
      setIsAppleLoading(false);
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    setError(null);
    setEmailNotConfirmed(false);
    setResendSuccess(false);
    setIsSubmitting(true);

    try {
      const { error } = await signIn(data.email, data.password);

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          setError(t('login.invalidCredentials'));
        } else if (error.message.includes('Email not confirmed')) {
          setError(t('login.emailNotConfirmed'));
          setEmailNotConfirmed(true);
        } else {
          setError(error.message);
        }
        return;
      }

      // Redirect to saved URL or home
      const redirectUrl = getRedirectUrl();
      navigate(redirectUrl);
    } catch {
      setError(t('common.unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    const email = getValues('email');
    if (!email) return;

    setResendLoading(true);
    setResendSuccess(false);

    try {
      const { error } = await resendVerificationEmail(email);
      if (error) {
        setError(error.message);
      } else {
        setResendSuccess(true);
        setError(null);
        setEmailNotConfirmed(false);
      }
    } catch {
      setError(t('common.unexpectedError'));
    } finally {
      setResendLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center px-4 py-12">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src="https://parentpath.de/pp_logo-piktogram-w100.svg"
            alt="ParentPath"
            className="w-16 h-16"
          />
        </div>

      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="glass-effect py-8 px-6 rounded-[15px] border border-white/25">
          {/* Verified Success Message */}
          {verified && (
            <div className="mb-6 p-4 bg-green-500/20 border border-green-500/30 rounded-[10px] flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-300">
                  {t('login.emailVerifiedTitle')}
                </p>
                <p className="text-sm text-green-300/80 mt-1">
                  {t('login.emailVerifiedMessage')}
                </p>
              </div>
            </div>
          )}

          {/* Resend Verification Success */}
          {resendSuccess && (
            <div className="mb-6 p-4 bg-green-500/20 border border-green-500/30 rounded-[10px] flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-300">
                  {t('login.resendSuccessTitle')}
                </p>
                <p className="text-sm text-green-300/80 mt-1">
                  {t('login.resendSuccessMessage')}
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-[10px]">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-300">{error}</p>
              </div>
              {emailNotConfirmed && (
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                  className="mt-3 w-full py-2 px-4 rounded-[8px] bg-white/10 border border-white/20
                             text-sm font-medium text-white hover:bg-white/15 transition-colors
                             disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {resendLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {t('login.resendVerificationEmail')}
                </button>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Email Input */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1.5">
                {t('common.emailLabel')}
              </label>
              <div className="relative">
                <input
                  {...register('email')}
                  type="email"
                  placeholder={t('common.emailPlaceholder')}
                  autoComplete="email"
                  className="w-full px-4 py-3 pr-12 rounded-[10px] bg-white/10 border border-white/20
                             text-white placeholder-white/40 caret-white
                             focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                             outline-none transition-all"
                />
                <Mail className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
              </div>
              {errors.email && (
                <p className="mt-1.5 text-sm text-red-400">{errors.email.message}</p>
              )}
            </div>

            {/* Password Input */}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1.5">
                {t('common.passwordLabel')}
              </label>
              <div className="relative">
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  className="w-full px-4 py-3 pr-12 rounded-[10px] bg-white/10 border border-white/20
                             text-white placeholder-white/40 caret-white
                             focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                             outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-sm text-red-400">{errors.password.message}</p>
              )}
            </div>

            {/* Forgot Password Link */}
            <div className="flex justify-end">
              <Link
                to="/reset-password"
                className="text-sm font-medium text-pp-orange hover:text-pp-orange/80"
              >
                {t('login.forgotPassword')}
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold
                         hover:bg-pp-orange/90 transition-colors disabled:opacity-50
                         flex items-center justify-center"
            >
              {isSubmitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t('login.signIn')
              )}
            </button>
          </form>

          {/* OAuth Sign-In Section */}
          {(appleEnabled || googleEnabled) && (
            <>
              <div className="my-6 flex justify-center text-sm">
                <span className="text-white/50">
                  {t('login.orContinueWith')}
                </span>
              </div>

              <div className="space-y-3">
                {/* Apple Sign-In Button */}
                {appleEnabled && (
                  <button
                    type="button"
                    onClick={handleAppleSignIn}
                    disabled={isAppleLoading || isSubmitting || isGoogleLoading}
                    className="w-full py-3 px-4 rounded-[10px] bg-black text-white font-medium
                               hover:bg-gray-900 transition-colors disabled:opacity-50
                               flex items-center justify-center gap-3"
                  >
                    {isAppleLoading ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                        </svg>
                        {t('login.signInWithApple')}
                      </>
                    )}
                  </button>
                )}

                {/* Google Sign-In Button */}
                {googleEnabled && (
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={isGoogleLoading || isSubmitting || isAppleLoading}
                    className="w-full py-3 px-4 rounded-[10px] bg-white text-gray-900 font-medium
                               hover:bg-gray-100 transition-colors disabled:opacity-50
                               flex items-center justify-center gap-3"
                  >
                    {isGoogleLoading ? (
                      <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                          <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          />
                          <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          />
                          <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          />
                          <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          />
                        </svg>
                        {t('login.signInWithGoogle')}
                      </>
                    )}
                  </button>
                )}
              </div>
            </>
          )}

          {/* Register Link */}
          <p className="mt-6 text-center text-sm text-white/70">
            {t('login.noAccount')}{' '}
            <Link
              to="/register"
              className="font-medium text-pp-orange hover:text-pp-orange/80"
            >
              {t('login.registerNow')}
            </Link>
          </p>
        </div>

        {/* Legal Links */}
        <div className="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-2 text-[1rem]">
          <Link to="/legal/imprint" className="text-pp-orange hover:text-pp-orange/80 transition-colors">
            {t('common.imprint')}
          </Link>
          <Link to="/legal/privacy" className="text-pp-orange hover:text-pp-orange/80 transition-colors">
            {t('common.privacy')}
          </Link>
          <Link to="/legal/terms" className="text-pp-orange hover:text-pp-orange/80 transition-colors">
            {t('common.terms')}
          </Link>
        </div>
      </div>
    </div>
  );
}
